/**
 * The PDF writer's default options, and the text-only layout built on them, in a module of their
 * own so a page can build `formatOptions` without importing the writer (fonts, layout, emitter)
 * into its bundle. `src/formats/write/pdf.ts` re-exports `PDF_DEFAULTS` from here.
 */

import type { PdfFormatOptions } from '../../../contracts/cue-model.ts';

export const PDF_DEFAULTS: PdfFormatOptions = {
  format: 'pdf',
  pageSize: 'a4',
  orientation: 'portrait',
  marginsPt: { top: 56, right: 48, bottom: 56, left: 48 },
  columns: ['index', 'timecode', 'text'],
  bodyFontFamily: 'Helvetica',
  bodyFontSizePt: 11,
  lineSpacing: 1.25,
  header: null,
  footer: null,
  pageNumbers: true,
  styled: true,
};

/**
 * A PDF's layout as the save step names it (SEO_BRIEF_08 section 7.1), the way a Word document
 * has `DocxFormatOptions.layout`: `'times'` is the writer's default table (number, times, text),
 * `'text'` the words alone (`columns: ['text']`).
 */
export type PdfLayout = 'times' | 'text';

/** Anything but the exact value 'text' is the table with times, the writer's default. */
export function pdfLayoutOf(value: string | null | undefined): PdfLayout {
  return value === 'text' ? 'text' : 'times';
}

/** The writer's columns for a layout. */
export function pdfColumns(layout: PdfLayout): PdfFormatOptions['columns'] {
  return layout === 'text' ? ['text'] : PDF_DEFAULTS.columns;
}

/** The three text sizes /txt-to-pdf and /text-to-pdf offer (section 7.3), in points. */
export const PDF_TEXT_SIZES = { small: 9.5, normal: 11, large: 13.5 } as const;
export type PdfTextSize = keyof typeof PDF_TEXT_SIZES;

/** What the two text pages ask for: page size, text size, an optional title, page numbers. */
export interface PdfDocumentChoices {
  readonly pageSize: 'a4' | 'letter';
  readonly textSize: PdfTextSize;
  /** Printed at the top of the page (the writer's `header`); empty or blank is none. */
  readonly title: string;
  readonly pageNumbers: boolean;
}

/** The writer's options for a plain-text document: the text-only layout and the page's choices. */
export function pdfDocumentOptions(choices: PdfDocumentChoices): PdfFormatOptions {
  const title = choices.title.replace(/\s+/g, ' ').trim();
  return {
    ...PDF_DEFAULTS,
    pageSize: choices.pageSize,
    columns: pdfColumns('text'),
    bodyFontSizePt: PDF_TEXT_SIZES[choices.textSize] ?? PDF_TEXT_SIZES.normal,
    header: title === '' ? null : title,
    pageNumbers: choices.pageNumbers,
  };
}

/**
 * Letter for a browser set to US English, A4 everywhere else (section 7.3). `languages` is
 * `navigator.languages`, most preferred first; only the first decides.
 */
export function defaultPageSize(languages: readonly string[] | undefined): 'a4' | 'letter' {
  const first = (languages?.[0] ?? '').toLowerCase();
  return first === 'en-us' ? 'letter' : 'a4';
}
